import React, { useEffect, useState } from "react";
import { appFetchJson } from "../../../utils/http";
import apiRoutes from "../../../api/apiRoutes";
import { getActorType } from "../../../utils/auth"; // Importamos getActorType

// Helper para fecha
const formatoFechaDetalle = (fecha) => {
  if (!fecha) return "";
  try {
    const d = new Date(fecha);
    if (isNaN(d.getTime())) return fecha;
    
    const fechaPart = d.toLocaleDateString("es-PY", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    }).replace(/ /g, "-").replace(".", "");

    const horaPart = d.toLocaleTimeString("es-PY", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });

    return `${fechaPart} ${horaPart}`;
  } catch (e) {
    return fecha;
  }
};

export default function VerAuditoria({ id, onClose = () => {} }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  const actorType = getActorType();

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setErr("");
      try {
        const url = apiRoutes.auditoria?.retrieveUrl 
          ? apiRoutes.auditoria.retrieveUrl(id)
          : `/api/auditoria/get/${id}`;

        const resp = await appFetchJson(url);

        if (!cancelled) {
          if (!resp) {
             setErr("Registro no encontrado o sin permisos.");
          } else {
             setData(resp);
          }
        }
      } catch (error) {
        console.error("Error al cargar Auditoría:", error);
        if (!cancelled) setErr("Error al cargar el detalle.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [id]);

  // Definición de filas. 
  // Agregamos una propiedad 'isCode' o 'longScroll' para identificar campos largos
  const allRows = [
    { label: "Cód. Audit (ID)", value: data?.codigoSqCod || data?.id },
    { label: "Fecha", value: formatoFechaDetalle(data?.dataFecha) },
    { label: "Usuario", value: data?.descricaoUsuario },
    { label: "Tabla Afectada", value: data?.codigoTabla },
    { label: "Operación", value: data?.codigoTipo }, 
    { label: "Origen", value: data?.descricaoOrigen },
    // TAREA: Activamos el flag 'longScroll' para que tenga su propia cajita con scroll
    { label: "Tarea", value: data?.descricaoTarea, longScroll: true }, 
  ];

  // Filtrar filas si es CLIENTE
  const rows = actorType === "CLIENTE"
    ? allRows.filter(row => ["Operación", "Fecha", "Usuario", "Tarea"].includes(row.label))
    : allRows;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        
        {/* HEADER */}
        <div className="modal-header">
          <h2 className="section-title">Detalle de Auditoría</h2>
        </div>

        {/* BODY */}
        <div className="modal-body">
          {loading && (
            <div className="py-8 text-center text-gray-500">Cargando información...</div>
          )}

          {!loading && err && (
            <div className="py-8 text-center text-red-600 font-medium">{err}</div>
          )}

          {!loading && !err && data && (
            // CAMBIO: Usamos 'flex flex-col gap-4' en lugar de 'form-grid' 
            // para asegurar que estén uno debajo del otro y no en grilla.
            <div className="flex flex-col gap-3 p-2">
               {rows.map((row, idx) => (
                 <Fila 
                   key={idx} 
                   label={row.label} 
                   value={row.value} 
                   longScroll={row.longScroll} // Pasamos el flag
                 />
               ))}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <div className="flex justify-end">
            <button className="btn-fil" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

// Componente Fila Mejorado
function Fila({ label, value, longScroll }) {
  return (
    // width full asegura que ocupe todo el ancho (uno debajo del otro)
    <div className="w-full border-b border-gray-100 pb-2 last:border-0"> 
      <div className="form-label mb-1 text-gray-700 font-semibold">{label}:</div>
      
      {longScroll ? (
        // ESTILO PARA CAMPO LARGO (TAREA):
        // max-h-60: Altura máxima fija (aprox 15 lineas)
        // overflow-y-auto: Muestra scroll si se pasa de la altura
        // bg-gray-50/rounded/border: Le da aspecto de caja de código/log
        <div className="w-full max-h-60 overflow-y-auto bg-gray-50 border border-gray-200 rounded p-3 text-xs font-mono text-gray-800 break-all whitespace-pre-wrap shadow-inner">
           {value || <span className="text-gray-400">—</span>}
        </div>
      ) : (
        // ESTILO NORMAL
        <div className="value break-words text-sm text-gray-600">
          {value !== null && value !== undefined && value !== "" 
            ? value 
            : <span className="text-gray-400">—</span>}
        </div>
      )}
    </div>
  );
}